/* Affiliate partner cabinet i18n.

   Same 7 languages as the bot/landing. Russian last per product preference.
   Adding a key: add to `en` first, then translate downstream.
   `t(key, vars?)` does {placeholder} substitution. */

export const DEFAULT_LANG = "en";

// Order shown in the picker. Keep `ru` last.
export const LANGS = ["en", "es", "de", "fr", "tr", "pt", "ru"];

export const LANG_LABELS = {
  en: "English",
  es: "Español",
  de: "Deutsch",
  fr: "Français",
  tr: "Türkçe",
  pt: "Português",
  ru: "Русский",
};

export const LANG_FLAGS = {
  en: "🇬🇧",
  es: "🇪🇸",
  de: "🇩🇪",
  fr: "🇫🇷",
  tr: "🇹🇷",
  pt: "🇵🇹",
  ru: "🇷🇺",
};

const STORAGE_KEY = "clipra-affiliate-lang";

export function detectLang() {
  if (typeof window === "undefined") return DEFAULT_LANG;
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (stored && LANGS.includes(stored)) return stored;
  } catch {
    /* ignore */
  }
  const nav = (navigator.language || "en").slice(0, 2).toLowerCase();
  return LANGS.includes(nav) ? nav : DEFAULT_LANG;
}

export function persistLang(lang) {
  try {
    window.localStorage.setItem(STORAGE_KEY, lang);
  } catch {
    /* ignore */
  }
}

const STRINGS = {
  en: {
    // ── Common
    "common.cancel": "Cancel",
    "common.close": "Close",
    "common.save": "Save",
    "common.loading": "Loading…",
    "common.optional": "Optional",
    "common.email": "Email",
    "common.password": "Password",
    "common.back_to_signin": "← Back to sign in",
    "common.sign_in": "Sign in",
    "common.sign_out": "Sign out",
    "common.apply": "Apply",
    "common.partners_subtitle": "// partners",
    "common.dash": "—",

    // ── Layout / nav
    "nav.dashboard": "Dashboard",
    "nav.codes": "My codes",
    "nav.transactions": "Transactions",
    "nav.payouts": "Payouts",
    "nav.profile": "Profile",
    "header.partner_cabinet": "// partner cabinet",
    "header.revenue_share_title": "Your revenue share",
    "header.theme_toggle_to": "Switch to {target} theme",

    // ── Login
    "login.subtitle": "// partners · sign in",
    "login.placeholder.email": "you@example.com",
    "login.busy": "Authenticating…",
    "login.cta": "Sign in",
    "login.forgot": "Forgot password?",
    "login.new_to": "New to Clipra Partners?",
    "login.apply": "Apply",

    // ── Signup
    "signup.subtitle.apply": "// partners · apply",
    "signup.subtitle.claim": "// partners · claim invite",
    "signup.name": "Name",
    "signup.placeholder.name": "Your name or channel",
    "signup.placeholder.password": "At least 8 characters",
    "signup.invite_hint":
      "Claiming invite — admin pre-set your commission and currency.",
    "signup.busy.creating": "Creating account…",
    "signup.cta.create": "Create account",
    "signup.cta.claim": "Claim invite",
    "signup.already": "Already have an account?",

    // ── Forgot password
    "forgot.subtitle": "// partners · reset password",
    "forgot.intro":
      "Enter the email you signed up with — we'll send a one-hour reset link.",
    "forgot.busy": "Sending…",
    "forgot.cta": "Send reset link",
    "forgot.sent.body":
      "If an account exists for {email}, you'll receive a password-reset link within a minute.",
    "forgot.sent.spam": "Didn't get it? Check your spam folder, or",
    "forgot.sent.try_again": "try again",

    // ── Reset password
    "reset.subtitle": "// partners · choose a new password",
    "reset.new": "New password",
    "reset.confirm": "Confirm password",
    "reset.placeholder.confirm": "Re-enter password",
    "reset.err.missing_token": "Missing reset token in URL",
    "reset.err.too_short": "Password must be at least 8 characters",
    "reset.err.mismatch": "Passwords don't match",
    "reset.busy": "Saving…",
    "reset.cta": "Set new password",
    "reset.done": "✅ Password updated. Redirecting to sign-in…",

    // ── Dashboard
    "dash.welcome": "Welcome, {name}",
    "dash.subtitle": "// {pct}% revenue share · {currency} display",
    "dash.kpi.signups": "Sign-ups",
    "dash.kpi.active": "Active payers",
    "dash.kpi.earned": "Earned",
    "dash.kpi.pending": "Pending payout",
    "dash.how.title": "// how it works",
    "dash.how.step1": "Create one or more codes in",
    "dash.how.step1_link": "My codes",
    "dash.how.step1_dot": ".",
    "dash.how.step2_prefix": "Share the link",
    "dash.how.step2_suffix": "in your channel.",
    "dash.how.step3":
      "When attributed users pay for Pro, you earn {pct}% revenue share.",
    "dash.how.step4": "Request a payout once your balance reaches ${threshold}.",
    "dash.payout_requested": "Payout requested.",
    "dash.payout_requested_hint": "Our team will process it shortly.",

    // ── Codes
    "codes.title": "My codes",
    "codes.subtitle": "// one code per channel · share to attribute users",
    "codes.create": "Create code",
    "codes.empty": "No codes yet — create your first one to start attributing users.",
    "codes.col.code": "Code",
    "codes.col.label": "Label",
    "codes.col.signups": "Sign-ups",
    "codes.col.earned": "Earned",
    "codes.col.status": "Status",
    "codes.row.links": "Links",
    "codes.row.hide": "Hide",
    "codes.row.enable": "Enable",
    "codes.row.disable": "Disable",
    "codes.link.telegram": "Telegram",
    "codes.link.web": "Web (ref)",
    "codes.modal.title": "New code",
    "codes.modal.code_label": "Code (3–32 chars · A–Z, 0–9, _, -)",
    "codes.modal.code_placeholder": "MYCHANNEL26",
    "codes.modal.label_label": "Label (optional)",
    "codes.modal.label_placeholder": "YouTube channel",
    "codes.modal.busy": "Creating…",
    "codes.modal.cta": "Create",
    "codes.created": "Code created",
    "codes.activated": "Code active",
    "codes.disabled": "Code disabled",
    "codes.no_label": "no label",

    // ── Transactions
    "tx.title": "Transactions",
    "tx.subtitle": "// every paid attribution · do a control-purchase to verify",
    "tx.total": "Total earned (period)",
    "tx.tip":
      "💡 Tip: Make a small purchase using your own affiliate link to verify the attribution. The transaction will show up here within seconds — that's your proof the system is working.",
    "tx.col.datetime": "Date · time",
    "tx.col.user": "User",
    "tx.col.payment": "Payment",
    "tx.col.usd": "USD",
    "tx.col.commission": "% · commission",
    "tx.empty":
      "No transactions yet — when an attributed user pays, the commission will appear here.",

    // ── Payouts
    "payouts.title": "Payouts",
    "payouts.subtitle":
      "// request when balance ≥ ${threshold} · paid manually",
    "payouts.available": "Available balance",
    "payouts.threshold": "Threshold:",
    "payouts.cta": "Request payout",
    "payouts.cta.pending": "Pending request",
    "payouts.cta.below": "Below threshold",
    "payouts.cta.busy": "Requesting…",
    "payouts.requested": "Payout requested",
    "payouts.col.date": "Date",
    "payouts.col.usd": "USD",
    "payouts.col.native": "Native",
    "payouts.col.method": "Method",
    "payouts.col.status": "Status",
    "payouts.col.note": "Note",
    "payouts.empty": "No payouts yet.",

    // ── Profile
    "profile.title": "Profile",
    "profile.subtitle": "// your settings & payout details",
    "profile.name": "Name",
    "profile.email": "Email",
    "profile.email_hint": "Contact support to change your email.",
    "profile.commission": "Commission",
    "profile.commission_hint": "Set by admin. Contact support to renegotiate.",
    "profile.currency": "Display currency",
    "profile.currency_hint":
      "How your balance is displayed. Conversion happens at request time.",
    "profile.channels": "Distribution channels",
    "profile.channels_placeholder": "YouTube, Telegram channel…",
    "profile.payout_section": "// payout details",
    "profile.payout_intro":
      "Provide at least one method so the team can pay you when you request a payout.",
    "profile.payout.phone": "Phone",
    "profile.payout.card": "Card number",
    "profile.payout.bank_account": "Bank account",
    "profile.payout.ton_wallet": "TON wallet",
    "profile.payout.other": "Other (INN, IBAN…)",
    "profile.save": "Save profile",
    "profile.saving": "Saving…",
    "profile.saved": "Profile saved",
  },

  es: {
    "common.cancel": "Cancelar",
    "common.close": "Cerrar",
    "common.save": "Guardar",
    "common.loading": "Cargando…",
    "common.optional": "Opcional",
    "common.email": "Correo",
    "common.password": "Contraseña",
    "common.back_to_signin": "← Volver al inicio de sesión",
    "common.sign_in": "Iniciar sesión",
    "common.sign_out": "Cerrar sesión",
    "common.apply": "Aplicar",
    "common.partners_subtitle": "// socios",
    "common.dash": "—",
    "nav.dashboard": "Panel",
    "nav.codes": "Mis códigos",
    "nav.transactions": "Transacciones",
    "nav.payouts": "Pagos",
    "nav.profile": "Perfil",
    "header.partner_cabinet": "// área del socio",
    "header.revenue_share_title": "Tu participación de ingresos",
    "header.theme_toggle_to": "Cambiar a tema {target}",
    "login.subtitle": "// socios · iniciar sesión",
    "login.placeholder.email": "tu@ejemplo.com",
    "login.busy": "Autenticando…",
    "login.cta": "Iniciar sesión",
    "login.forgot": "¿Olvidaste tu contraseña?",
    "login.new_to": "¿Nuevo en Clipra Partners?",
    "login.apply": "Solicitar",
    "signup.subtitle.apply": "// socios · solicitar",
    "signup.subtitle.claim": "// socios · canjear invitación",
    "signup.name": "Nombre",
    "signup.placeholder.name": "Tu nombre o canal",
    "signup.placeholder.password": "Mínimo 8 caracteres",
    "signup.invite_hint":
      "Canjeando invitación — el admin ya configuró tu comisión y moneda.",
    "signup.busy.creating": "Creando cuenta…",
    "signup.cta.create": "Crear cuenta",
    "signup.cta.claim": "Canjear invitación",
    "signup.already": "¿Ya tienes cuenta?",
    "forgot.subtitle": "// socios · restablecer contraseña",
    "forgot.intro":
      "Introduce el correo con el que te registraste — enviaremos un enlace válido por una hora.",
    "forgot.busy": "Enviando…",
    "forgot.cta": "Enviar enlace de restablecimiento",
    "forgot.sent.body":
      "Si existe una cuenta para {email}, recibirás un enlace de restablecimiento en un minuto.",
    "forgot.sent.spam": "¿No lo recibiste? Revisa el spam, o",
    "forgot.sent.try_again": "intenta de nuevo",
    "reset.subtitle": "// socios · elige una nueva contraseña",
    "reset.new": "Nueva contraseña",
    "reset.confirm": "Confirmar contraseña",
    "reset.placeholder.confirm": "Reingresa la contraseña",
    "reset.err.missing_token": "Falta el token en la URL",
    "reset.err.too_short": "La contraseña debe tener al menos 8 caracteres",
    "reset.err.mismatch": "Las contraseñas no coinciden",
    "reset.busy": "Guardando…",
    "reset.cta": "Establecer nueva contraseña",
    "reset.done":
      "✅ Contraseña actualizada. Redirigiendo al inicio de sesión…",
    "dash.welcome": "Bienvenido, {name}",
    "dash.subtitle": "// {pct}% de ingresos · pantalla en {currency}",
    "dash.kpi.signups": "Registros",
    "dash.kpi.active": "Pagadores activos",
    "dash.kpi.earned": "Ganado",
    "dash.kpi.pending": "Pago pendiente",
    "dash.how.title": "// cómo funciona",
    "dash.how.step1": "Crea uno o más códigos en",
    "dash.how.step1_link": "Mis códigos",
    "dash.how.step1_dot": ".",
    "dash.how.step2_prefix": "Comparte el enlace",
    "dash.how.step2_suffix": "en tu canal.",
    "dash.how.step3":
      "Cuando los usuarios atribuidos pagan por Pro, ganas {pct}% de los ingresos.",
    "dash.how.step4":
      "Solicita un pago cuando tu saldo alcance ${threshold}.",
    "dash.payout_requested": "Pago solicitado.",
    "dash.payout_requested_hint": "Nuestro equipo lo procesará pronto.",
    "codes.title": "Mis códigos",
    "codes.subtitle":
      "// un código por canal · compártelo para atribuir usuarios",
    "codes.create": "Crear código",
    "codes.empty":
      "Aún no hay códigos — crea el primero para empezar a atribuir usuarios.",
    "codes.col.code": "Código",
    "codes.col.label": "Etiqueta",
    "codes.col.signups": "Registros",
    "codes.col.earned": "Ganado",
    "codes.col.status": "Estado",
    "codes.row.links": "Enlaces",
    "codes.row.hide": "Ocultar",
    "codes.row.enable": "Activar",
    "codes.row.disable": "Desactivar",
    "codes.link.telegram": "Telegram",
    "codes.link.web": "Web (ref)",
    "codes.modal.title": "Nuevo código",
    "codes.modal.code_label": "Código (3–32 caracteres · A–Z, 0–9, _, -)",
    "codes.modal.code_placeholder": "MICANAL26",
    "codes.modal.label_label": "Etiqueta (opcional)",
    "codes.modal.label_placeholder": "Canal de YouTube",
    "codes.modal.busy": "Creando…",
    "codes.modal.cta": "Crear",
    "codes.created": "Código creado",
    "codes.activated": "Código activo",
    "codes.disabled": "Código desactivado",
    "codes.no_label": "sin etiqueta",
    "tx.title": "Transacciones",
    "tx.subtitle":
      "// cada atribución pagada · haz una compra de control para verificar",
    "tx.total": "Total ganado (período)",
    "tx.tip":
      "💡 Consejo: Haz una pequeña compra usando tu propio enlace para verificar la atribución. La transacción aparecerá aquí en segundos — esa es tu prueba.",
    "tx.col.datetime": "Fecha · hora",
    "tx.col.user": "Usuario",
    "tx.col.payment": "Pago",
    "tx.col.usd": "USD",
    "tx.col.commission": "% · comisión",
    "tx.empty":
      "Aún no hay transacciones — cuando un usuario atribuido pague, la comisión aparecerá aquí.",
    "payouts.title": "Pagos",
    "payouts.subtitle":
      "// solicita cuando el saldo ≥ ${threshold} · pago manual",
    "payouts.available": "Saldo disponible",
    "payouts.threshold": "Umbral:",
    "payouts.cta": "Solicitar pago",
    "payouts.cta.pending": "Solicitud pendiente",
    "payouts.cta.below": "Bajo el umbral",
    "payouts.cta.busy": "Solicitando…",
    "payouts.requested": "Pago solicitado",
    "payouts.col.date": "Fecha",
    "payouts.col.usd": "USD",
    "payouts.col.native": "Nativo",
    "payouts.col.method": "Método",
    "payouts.col.status": "Estado",
    "payouts.col.note": "Nota",
    "payouts.empty": "Aún no hay pagos.",
    "profile.title": "Perfil",
    "profile.subtitle": "// tu configuración y datos de pago",
    "profile.name": "Nombre",
    "profile.email": "Correo",
    "profile.email_hint": "Contacta a soporte para cambiar tu correo.",
    "profile.commission": "Comisión",
    "profile.commission_hint":
      "Establecida por el admin. Contacta a soporte para renegociar.",
    "profile.currency": "Moneda de visualización",
    "profile.currency_hint":
      "Cómo se muestra tu saldo. La conversión ocurre al solicitar pago.",
    "profile.channels": "Canales de distribución",
    "profile.channels_placeholder": "YouTube, canal de Telegram…",
    "profile.payout_section": "// datos de pago",
    "profile.payout_intro":
      "Indica al menos un método para que podamos pagarte cuando solicites el retiro.",
    "profile.payout.phone": "Teléfono",
    "profile.payout.card": "Número de tarjeta",
    "profile.payout.bank_account": "Cuenta bancaria",
    "profile.payout.ton_wallet": "Billetera TON",
    "profile.payout.other": "Otro (NIT, IBAN…)",
    "profile.save": "Guardar perfil",
    "profile.saving": "Guardando…",
    "profile.saved": "Perfil guardado",
  },

  de: {
    "common.cancel": "Abbrechen",
    "common.close": "Schließen",
    "common.save": "Speichern",
    "common.loading": "Lädt…",
    "common.optional": "Optional",
    "common.email": "E-Mail",
    "common.password": "Passwort",
    "common.back_to_signin": "← Zurück zur Anmeldung",
    "common.sign_in": "Anmelden",
    "common.sign_out": "Abmelden",
    "common.apply": "Anwenden",
    "common.partners_subtitle": "// partner",
    "common.dash": "—",
    "nav.dashboard": "Übersicht",
    "nav.codes": "Meine Codes",
    "nav.transactions": "Transaktionen",
    "nav.payouts": "Auszahlungen",
    "nav.profile": "Profil",
    "header.partner_cabinet": "// partnerbereich",
    "header.revenue_share_title": "Deine Umsatzbeteiligung",
    "header.theme_toggle_to": "Zu {target} Modus wechseln",
    "login.subtitle": "// partner · anmelden",
    "login.placeholder.email": "du@beispiel.com",
    "login.busy": "Authentifiziere…",
    "login.cta": "Anmelden",
    "login.forgot": "Passwort vergessen?",
    "login.new_to": "Neu bei Clipra Partners?",
    "login.apply": "Bewerben",
    "signup.subtitle.apply": "// partner · bewerben",
    "signup.subtitle.claim": "// partner · einladung einlösen",
    "signup.name": "Name",
    "signup.placeholder.name": "Dein Name oder Kanal",
    "signup.placeholder.password": "Mindestens 8 Zeichen",
    "signup.invite_hint":
      "Einladung einlösen — der Admin hat deine Provision und Währung schon gesetzt.",
    "signup.busy.creating": "Konto wird erstellt…",
    "signup.cta.create": "Konto erstellen",
    "signup.cta.claim": "Einladung einlösen",
    "signup.already": "Schon registriert?",
    "forgot.subtitle": "// partner · passwort zurücksetzen",
    "forgot.intro":
      "Gib die E-Mail an, mit der du dich registriert hast — wir senden einen 1-Stunden-Link.",
    "forgot.busy": "Senden…",
    "forgot.cta": "Reset-Link senden",
    "forgot.sent.body":
      "Falls ein Konto für {email} existiert, erhältst du den Link innerhalb einer Minute.",
    "forgot.sent.spam": "Nicht erhalten? Prüfe den Spam-Ordner, oder",
    "forgot.sent.try_again": "erneut versuchen",
    "reset.subtitle": "// partner · neues passwort wählen",
    "reset.new": "Neues Passwort",
    "reset.confirm": "Passwort bestätigen",
    "reset.placeholder.confirm": "Passwort erneut eingeben",
    "reset.err.missing_token": "Reset-Token fehlt in der URL",
    "reset.err.too_short": "Passwort muss mindestens 8 Zeichen haben",
    "reset.err.mismatch": "Passwörter stimmen nicht überein",
    "reset.busy": "Speichern…",
    "reset.cta": "Neues Passwort setzen",
    "reset.done":
      "✅ Passwort aktualisiert. Weiterleitung zur Anmeldung…",
    "dash.welcome": "Willkommen, {name}",
    "dash.subtitle": "// {pct}% Umsatzbeteiligung · {currency} Anzeige",
    "dash.kpi.signups": "Anmeldungen",
    "dash.kpi.active": "Aktive Zahler",
    "dash.kpi.earned": "Verdient",
    "dash.kpi.pending": "Ausstehende Auszahlung",
    "dash.how.title": "// so funktioniert es",
    "dash.how.step1": "Erstelle einen oder mehrere Codes in",
    "dash.how.step1_link": "Meine Codes",
    "dash.how.step1_dot": ".",
    "dash.how.step2_prefix": "Teile den Link",
    "dash.how.step2_suffix": "in deinem Kanal.",
    "dash.how.step3":
      "Wenn zugeordnete Nutzer Pro kaufen, verdienst du {pct}% Umsatzbeteiligung.",
    "dash.how.step4":
      "Fordere eine Auszahlung, sobald dein Guthaben ${threshold} erreicht.",
    "dash.payout_requested": "Auszahlung angefordert.",
    "dash.payout_requested_hint": "Unser Team bearbeitet sie zeitnah.",
    "codes.title": "Meine Codes",
    "codes.subtitle":
      "// ein Code pro Kanal · teilen, um Nutzer zuzuordnen",
    "codes.create": "Code erstellen",
    "codes.empty":
      "Noch keine Codes — erstelle deinen ersten, um Nutzer zuzuordnen.",
    "codes.col.code": "Code",
    "codes.col.label": "Bezeichnung",
    "codes.col.signups": "Anmeldungen",
    "codes.col.earned": "Verdient",
    "codes.col.status": "Status",
    "codes.row.links": "Links",
    "codes.row.hide": "Verbergen",
    "codes.row.enable": "Aktivieren",
    "codes.row.disable": "Deaktivieren",
    "codes.link.telegram": "Telegram",
    "codes.link.web": "Web (ref)",
    "codes.modal.title": "Neuer Code",
    "codes.modal.code_label": "Code (3–32 Zeichen · A–Z, 0–9, _, -)",
    "codes.modal.code_placeholder": "MEINKANAL26",
    "codes.modal.label_label": "Bezeichnung (optional)",
    "codes.modal.label_placeholder": "YouTube-Kanal",
    "codes.modal.busy": "Erstellen…",
    "codes.modal.cta": "Erstellen",
    "codes.created": "Code erstellt",
    "codes.activated": "Code aktiv",
    "codes.disabled": "Code deaktiviert",
    "codes.no_label": "keine Bezeichnung",
    "tx.title": "Transaktionen",
    "tx.subtitle":
      "// jede bezahlte Zuordnung · Test-Kauf zur Verifizierung",
    "tx.total": "Gesamtverdienst (Zeitraum)",
    "tx.tip":
      "💡 Tipp: Mache einen kleinen Testkauf mit deinem eigenen Link, um die Zuordnung zu prüfen. Die Transaktion erscheint in Sekunden hier.",
    "tx.col.datetime": "Datum · Uhrzeit",
    "tx.col.user": "Nutzer",
    "tx.col.payment": "Zahlung",
    "tx.col.usd": "USD",
    "tx.col.commission": "% · Provision",
    "tx.empty":
      "Noch keine Transaktionen — sobald ein zugeordneter Nutzer zahlt, erscheint die Provision hier.",
    "payouts.title": "Auszahlungen",
    "payouts.subtitle":
      "// anfordern bei Guthaben ≥ ${threshold} · manuelle Auszahlung",
    "payouts.available": "Verfügbares Guthaben",
    "payouts.threshold": "Schwelle:",
    "payouts.cta": "Auszahlung anfordern",
    "payouts.cta.pending": "Anfrage ausstehend",
    "payouts.cta.below": "Unter der Schwelle",
    "payouts.cta.busy": "Anfordern…",
    "payouts.requested": "Auszahlung angefordert",
    "payouts.col.date": "Datum",
    "payouts.col.usd": "USD",
    "payouts.col.native": "Lokal",
    "payouts.col.method": "Methode",
    "payouts.col.status": "Status",
    "payouts.col.note": "Notiz",
    "payouts.empty": "Noch keine Auszahlungen.",
    "profile.title": "Profil",
    "profile.subtitle": "// deine Einstellungen & Auszahlungsdaten",
    "profile.name": "Name",
    "profile.email": "E-Mail",
    "profile.email_hint":
      "Kontaktiere den Support, um deine E-Mail zu ändern.",
    "profile.commission": "Provision",
    "profile.commission_hint":
      "Vom Admin festgelegt. Support kontaktieren zum Neuverhandeln.",
    "profile.currency": "Anzeigewährung",
    "profile.currency_hint":
      "Wie dein Guthaben angezeigt wird. Umrechnung erfolgt bei Auszahlung.",
    "profile.channels": "Vertriebskanäle",
    "profile.channels_placeholder": "YouTube, Telegram-Kanal…",
    "profile.payout_section": "// auszahlungsdaten",
    "profile.payout_intro":
      "Gib mindestens eine Methode an, damit wir dich bei einer Anfrage bezahlen können.",
    "profile.payout.phone": "Telefon",
    "profile.payout.card": "Kartennummer",
    "profile.payout.bank_account": "Bankkonto",
    "profile.payout.ton_wallet": "TON Wallet",
    "profile.payout.other": "Sonstiges (IBAN, BIC…)",
    "profile.save": "Profil speichern",
    "profile.saving": "Speichern…",
    "profile.saved": "Profil gespeichert",
  },

  fr: {
    "common.cancel": "Annuler",
    "common.close": "Fermer",
    "common.save": "Enregistrer",
    "common.loading": "Chargement…",
    "common.optional": "Facultatif",
    "common.email": "E-mail",
    "common.password": "Mot de passe",
    "common.back_to_signin": "← Retour à la connexion",
    "common.sign_in": "Se connecter",
    "common.sign_out": "Se déconnecter",
    "common.apply": "Appliquer",
    "common.partners_subtitle": "// partenaires",
    "common.dash": "—",
    "nav.dashboard": "Tableau de bord",
    "nav.codes": "Mes codes",
    "nav.transactions": "Transactions",
    "nav.payouts": "Paiements",
    "nav.profile": "Profil",
    "header.partner_cabinet": "// espace partenaire",
    "header.revenue_share_title": "Votre part des revenus",
    "header.theme_toggle_to": "Passer au thème {target}",
    "login.subtitle": "// partenaires · connexion",
    "login.placeholder.email": "vous@exemple.com",
    "login.busy": "Authentification…",
    "login.cta": "Se connecter",
    "login.forgot": "Mot de passe oublié ?",
    "login.new_to": "Nouveau sur Clipra Partners ?",
    "login.apply": "Postuler",
    "signup.subtitle.apply": "// partenaires · postuler",
    "signup.subtitle.claim": "// partenaires · accepter l'invitation",
    "signup.name": "Nom",
    "signup.placeholder.name": "Votre nom ou chaîne",
    "signup.placeholder.password": "Au moins 8 caractères",
    "signup.invite_hint":
      "Acceptation d'invitation — l'admin a déjà défini votre commission et devise.",
    "signup.busy.creating": "Création du compte…",
    "signup.cta.create": "Créer un compte",
    "signup.cta.claim": "Accepter l'invitation",
    "signup.already": "Déjà inscrit ?",
    "forgot.subtitle": "// partenaires · réinitialiser le mot de passe",
    "forgot.intro":
      "Entrez l'e-mail utilisé à l'inscription — nous enverrons un lien valable 1 heure.",
    "forgot.busy": "Envoi…",
    "forgot.cta": "Envoyer le lien",
    "forgot.sent.body":
      "Si un compte existe pour {email}, vous recevrez un lien de réinitialisation dans la minute.",
    "forgot.sent.spam": "Pas reçu ? Vérifiez les spams, ou",
    "forgot.sent.try_again": "réessayez",
    "reset.subtitle": "// partenaires · choisir un nouveau mot de passe",
    "reset.new": "Nouveau mot de passe",
    "reset.confirm": "Confirmer le mot de passe",
    "reset.placeholder.confirm": "Ressaisir le mot de passe",
    "reset.err.missing_token": "Token manquant dans l'URL",
    "reset.err.too_short": "Le mot de passe doit faire au moins 8 caractères",
    "reset.err.mismatch": "Les mots de passe ne correspondent pas",
    "reset.busy": "Enregistrement…",
    "reset.cta": "Définir le nouveau mot de passe",
    "reset.done":
      "✅ Mot de passe mis à jour. Redirection vers la connexion…",
    "dash.welcome": "Bienvenue, {name}",
    "dash.subtitle": "// {pct}% de revenus · affichage en {currency}",
    "dash.kpi.signups": "Inscriptions",
    "dash.kpi.active": "Payeurs actifs",
    "dash.kpi.earned": "Gagné",
    "dash.kpi.pending": "Paiement en attente",
    "dash.how.title": "// comment ça marche",
    "dash.how.step1": "Créez un ou plusieurs codes dans",
    "dash.how.step1_link": "Mes codes",
    "dash.how.step1_dot": ".",
    "dash.how.step2_prefix": "Partagez le lien",
    "dash.how.step2_suffix": "sur votre chaîne.",
    "dash.how.step3":
      "Lorsque les utilisateurs attribués paient pour Pro, vous gagnez {pct}% des revenus.",
    "dash.how.step4":
      "Demandez un paiement dès que votre solde atteint ${threshold}.",
    "dash.payout_requested": "Paiement demandé.",
    "dash.payout_requested_hint": "Notre équipe le traitera rapidement.",
    "codes.title": "Mes codes",
    "codes.subtitle":
      "// un code par canal · partagez pour attribuer les utilisateurs",
    "codes.create": "Créer un code",
    "codes.empty":
      "Aucun code pour l'instant — créez le premier pour commencer.",
    "codes.col.code": "Code",
    "codes.col.label": "Étiquette",
    "codes.col.signups": "Inscriptions",
    "codes.col.earned": "Gagné",
    "codes.col.status": "Statut",
    "codes.row.links": "Liens",
    "codes.row.hide": "Masquer",
    "codes.row.enable": "Activer",
    "codes.row.disable": "Désactiver",
    "codes.link.telegram": "Telegram",
    "codes.link.web": "Web (ref)",
    "codes.modal.title": "Nouveau code",
    "codes.modal.code_label": "Code (3–32 caractères · A–Z, 0–9, _, -)",
    "codes.modal.code_placeholder": "MACHAINE26",
    "codes.modal.label_label": "Étiquette (facultatif)",
    "codes.modal.label_placeholder": "Chaîne YouTube",
    "codes.modal.busy": "Création…",
    "codes.modal.cta": "Créer",
    "codes.created": "Code créé",
    "codes.activated": "Code actif",
    "codes.disabled": "Code désactivé",
    "codes.no_label": "sans étiquette",
    "tx.title": "Transactions",
    "tx.subtitle":
      "// chaque attribution payée · faites un achat-test pour vérifier",
    "tx.total": "Total gagné (période)",
    "tx.tip":
      "💡 Astuce : Faites un petit achat via votre propre lien pour vérifier l'attribution. La transaction apparaîtra en quelques secondes.",
    "tx.col.datetime": "Date · heure",
    "tx.col.user": "Utilisateur",
    "tx.col.payment": "Paiement",
    "tx.col.usd": "USD",
    "tx.col.commission": "% · commission",
    "tx.empty":
      "Aucune transaction — quand un utilisateur attribué paie, la commission apparaît ici.",
    "payouts.title": "Paiements",
    "payouts.subtitle":
      "// demande possible si solde ≥ ${threshold} · paiement manuel",
    "payouts.available": "Solde disponible",
    "payouts.threshold": "Seuil :",
    "payouts.cta": "Demander un paiement",
    "payouts.cta.pending": "Demande en attente",
    "payouts.cta.below": "Sous le seuil",
    "payouts.cta.busy": "Demande…",
    "payouts.requested": "Paiement demandé",
    "payouts.col.date": "Date",
    "payouts.col.usd": "USD",
    "payouts.col.native": "Local",
    "payouts.col.method": "Méthode",
    "payouts.col.status": "Statut",
    "payouts.col.note": "Note",
    "payouts.empty": "Aucun paiement pour l'instant.",
    "profile.title": "Profil",
    "profile.subtitle": "// vos paramètres & coordonnées de paiement",
    "profile.name": "Nom",
    "profile.email": "E-mail",
    "profile.email_hint":
      "Contactez le support pour changer votre e-mail.",
    "profile.commission": "Commission",
    "profile.commission_hint":
      "Définie par l'admin. Contactez le support pour renégocier.",
    "profile.currency": "Devise d'affichage",
    "profile.currency_hint":
      "Comment votre solde est affiché. La conversion a lieu à la demande.",
    "profile.channels": "Canaux de distribution",
    "profile.channels_placeholder": "YouTube, chaîne Telegram…",
    "profile.payout_section": "// coordonnées de paiement",
    "profile.payout_intro":
      "Indiquez au moins une méthode pour que nous puissions vous payer.",
    "profile.payout.phone": "Téléphone",
    "profile.payout.card": "Numéro de carte",
    "profile.payout.bank_account": "Compte bancaire",
    "profile.payout.ton_wallet": "Portefeuille TON",
    "profile.payout.other": "Autre (SIRET, IBAN…)",
    "profile.save": "Enregistrer le profil",
    "profile.saving": "Enregistrement…",
    "profile.saved": "Profil enregistré",
  },

  tr: {
    "common.cancel": "İptal",
    "common.close": "Kapat",
    "common.save": "Kaydet",
    "common.loading": "Yükleniyor…",
    "common.optional": "İsteğe bağlı",
    "common.email": "E-posta",
    "common.password": "Şifre",
    "common.back_to_signin": "← Girişe dön",
    "common.sign_in": "Giriş yap",
    "common.sign_out": "Çıkış yap",
    "common.apply": "Uygula",
    "common.partners_subtitle": "// partnerler",
    "common.dash": "—",
    "nav.dashboard": "Panel",
    "nav.codes": "Kodlarım",
    "nav.transactions": "İşlemler",
    "nav.payouts": "Ödemeler",
    "nav.profile": "Profil",
    "header.partner_cabinet": "// partner paneli",
    "header.revenue_share_title": "Gelir paylaşım oranınız",
    "header.theme_toggle_to": "{target} temaya geç",
    "login.subtitle": "// partnerler · giriş",
    "login.placeholder.email": "siz@ornek.com",
    "login.busy": "Kimlik doğrulanıyor…",
    "login.cta": "Giriş yap",
    "login.forgot": "Şifrenizi mi unuttunuz?",
    "login.new_to": "Clipra Partners'da yeni misiniz?",
    "login.apply": "Başvur",
    "signup.subtitle.apply": "// partnerler · başvur",
    "signup.subtitle.claim": "// partnerler · davetiyeyi kullan",
    "signup.name": "Ad",
    "signup.placeholder.name": "Adınız veya kanalınız",
    "signup.placeholder.password": "En az 8 karakter",
    "signup.invite_hint":
      "Davetiye kullanılıyor — admin komisyon ve para biriminizi önceden ayarladı.",
    "signup.busy.creating": "Hesap oluşturuluyor…",
    "signup.cta.create": "Hesap oluştur",
    "signup.cta.claim": "Davetiyeyi kullan",
    "signup.already": "Zaten hesabınız var mı?",
    "forgot.subtitle": "// partnerler · şifre sıfırla",
    "forgot.intro":
      "Kayıt olduğunuz e-postayı girin — 1 saatlik bir sıfırlama linki göndereceğiz.",
    "forgot.busy": "Gönderiliyor…",
    "forgot.cta": "Sıfırlama linki gönder",
    "forgot.sent.body":
      "{email} için bir hesap varsa, bir dakika içinde şifre sıfırlama linki alacaksınız.",
    "forgot.sent.spam": "Almadın mı? Spam klasörünü kontrol et veya",
    "forgot.sent.try_again": "tekrar dene",
    "reset.subtitle": "// partnerler · yeni şifre seç",
    "reset.new": "Yeni şifre",
    "reset.confirm": "Şifreyi onayla",
    "reset.placeholder.confirm": "Şifreyi tekrar girin",
    "reset.err.missing_token": "URL'de sıfırlama tokenı eksik",
    "reset.err.too_short": "Şifre en az 8 karakter olmalı",
    "reset.err.mismatch": "Şifreler uyuşmuyor",
    "reset.busy": "Kaydediliyor…",
    "reset.cta": "Yeni şifre ayarla",
    "reset.done":
      "✅ Şifre güncellendi. Giriş ekranına yönlendiriliyorsunuz…",
    "dash.welcome": "Hoş geldin, {name}",
    "dash.subtitle": "// {pct}% gelir payı · {currency} görüntü",
    "dash.kpi.signups": "Kayıtlar",
    "dash.kpi.active": "Aktif ödeyenler",
    "dash.kpi.earned": "Kazanılan",
    "dash.kpi.pending": "Bekleyen ödeme",
    "dash.how.title": "// nasıl çalışır",
    "dash.how.step1": "Bir veya daha fazla kod oluştur:",
    "dash.how.step1_link": "Kodlarım",
    "dash.how.step1_dot": ".",
    "dash.how.step2_prefix": "Linki kanalında paylaş",
    "dash.how.step2_suffix": ".",
    "dash.how.step3":
      "Atfedilen kullanıcılar Pro'ya ödediğinde, {pct}% gelir payı kazanırsın.",
    "dash.how.step4":
      "Bakiye ${threshold} olduğunda ödeme talep edebilirsin.",
    "dash.payout_requested": "Ödeme talep edildi.",
    "dash.payout_requested_hint": "Ekibimiz kısa sürede işleyecek.",
    "codes.title": "Kodlarım",
    "codes.subtitle":
      "// kanal başına bir kod · kullanıcı atfı için paylaş",
    "codes.create": "Kod oluştur",
    "codes.empty":
      "Henüz kod yok — kullanıcı atfetmek için ilk kodu oluştur.",
    "codes.col.code": "Kod",
    "codes.col.label": "Etiket",
    "codes.col.signups": "Kayıtlar",
    "codes.col.earned": "Kazanılan",
    "codes.col.status": "Durum",
    "codes.row.links": "Linkler",
    "codes.row.hide": "Gizle",
    "codes.row.enable": "Etkinleştir",
    "codes.row.disable": "Devre dışı bırak",
    "codes.link.telegram": "Telegram",
    "codes.link.web": "Web (ref)",
    "codes.modal.title": "Yeni kod",
    "codes.modal.code_label": "Kod (3–32 karakter · A–Z, 0–9, _, -)",
    "codes.modal.code_placeholder": "KANALIM26",
    "codes.modal.label_label": "Etiket (isteğe bağlı)",
    "codes.modal.label_placeholder": "YouTube kanalı",
    "codes.modal.busy": "Oluşturuluyor…",
    "codes.modal.cta": "Oluştur",
    "codes.created": "Kod oluşturuldu",
    "codes.activated": "Kod aktif",
    "codes.disabled": "Kod devre dışı",
    "codes.no_label": "etiket yok",
    "tx.title": "İşlemler",
    "tx.subtitle":
      "// her ödenmiş atıf · doğrulamak için kontrol satın alımı yap",
    "tx.total": "Toplam kazanç (dönem)",
    "tx.tip":
      "💡 İpucu: Atfı doğrulamak için kendi linkinle küçük bir alım yap. İşlem saniyeler içinde burada görünecek.",
    "tx.col.datetime": "Tarih · saat",
    "tx.col.user": "Kullanıcı",
    "tx.col.payment": "Ödeme",
    "tx.col.usd": "USD",
    "tx.col.commission": "% · komisyon",
    "tx.empty":
      "Henüz işlem yok — atfedilen kullanıcı ödediğinde komisyon burada görünür.",
    "payouts.title": "Ödemeler",
    "payouts.subtitle":
      "// bakiye ≥ ${threshold} olduğunda talep et · manuel ödeme",
    "payouts.available": "Mevcut bakiye",
    "payouts.threshold": "Eşik:",
    "payouts.cta": "Ödeme talep et",
    "payouts.cta.pending": "Bekleyen talep",
    "payouts.cta.below": "Eşiğin altında",
    "payouts.cta.busy": "Talep ediliyor…",
    "payouts.requested": "Ödeme talep edildi",
    "payouts.col.date": "Tarih",
    "payouts.col.usd": "USD",
    "payouts.col.native": "Yerel",
    "payouts.col.method": "Yöntem",
    "payouts.col.status": "Durum",
    "payouts.col.note": "Not",
    "payouts.empty": "Henüz ödeme yok.",
    "profile.title": "Profil",
    "profile.subtitle": "// ayarlarınız & ödeme detayları",
    "profile.name": "Ad",
    "profile.email": "E-posta",
    "profile.email_hint":
      "E-postanızı değiştirmek için destek ile iletişime geçin.",
    "profile.commission": "Komisyon",
    "profile.commission_hint":
      "Admin tarafından ayarlanır. Yeniden müzakere için destek ile iletişime geçin.",
    "profile.currency": "Görüntü para birimi",
    "profile.currency_hint":
      "Bakiyeniz nasıl gösterilir. Dönüşüm talep anında yapılır.",
    "profile.channels": "Dağıtım kanalları",
    "profile.channels_placeholder": "YouTube, Telegram kanalı…",
    "profile.payout_section": "// ödeme detayları",
    "profile.payout_intro":
      "Talep ettiğinizde ödeme yapabilmemiz için en az bir yöntem belirtin.",
    "profile.payout.phone": "Telefon",
    "profile.payout.card": "Kart numarası",
    "profile.payout.bank_account": "Banka hesabı",
    "profile.payout.ton_wallet": "TON cüzdanı",
    "profile.payout.other": "Diğer (VKN, IBAN…)",
    "profile.save": "Profili kaydet",
    "profile.saving": "Kaydediliyor…",
    "profile.saved": "Profil kaydedildi",
  },

  pt: {
    "common.cancel": "Cancelar",
    "common.close": "Fechar",
    "common.save": "Salvar",
    "common.loading": "Carregando…",
    "common.optional": "Opcional",
    "common.email": "E-mail",
    "common.password": "Senha",
    "common.back_to_signin": "← Voltar ao login",
    "common.sign_in": "Entrar",
    "common.sign_out": "Sair",
    "common.apply": "Aplicar",
    "common.partners_subtitle": "// parceiros",
    "common.dash": "—",
    "nav.dashboard": "Painel",
    "nav.codes": "Meus códigos",
    "nav.transactions": "Transações",
    "nav.payouts": "Pagamentos",
    "nav.profile": "Perfil",
    "header.partner_cabinet": "// área do parceiro",
    "header.revenue_share_title": "Sua participação nos ganhos",
    "header.theme_toggle_to": "Mudar para tema {target}",
    "login.subtitle": "// parceiros · entrar",
    "login.placeholder.email": "voce@exemplo.com",
    "login.busy": "Autenticando…",
    "login.cta": "Entrar",
    "login.forgot": "Esqueceu a senha?",
    "login.new_to": "Novo no Clipra Partners?",
    "login.apply": "Inscrever-se",
    "signup.subtitle.apply": "// parceiros · inscrever-se",
    "signup.subtitle.claim": "// parceiros · resgatar convite",
    "signup.name": "Nome",
    "signup.placeholder.name": "Seu nome ou canal",
    "signup.placeholder.password": "Pelo menos 8 caracteres",
    "signup.invite_hint":
      "Resgatando convite — o admin já definiu sua comissão e moeda.",
    "signup.busy.creating": "Criando conta…",
    "signup.cta.create": "Criar conta",
    "signup.cta.claim": "Resgatar convite",
    "signup.already": "Já tem conta?",
    "forgot.subtitle": "// parceiros · redefinir senha",
    "forgot.intro":
      "Digite o e-mail usado no cadastro — enviaremos um link válido por 1 hora.",
    "forgot.busy": "Enviando…",
    "forgot.cta": "Enviar link de redefinição",
    "forgot.sent.body":
      "Se existir conta para {email}, você receberá um link de redefinição em um minuto.",
    "forgot.sent.spam": "Não recebeu? Verifique o spam, ou",
    "forgot.sent.try_again": "tente novamente",
    "reset.subtitle": "// parceiros · escolha uma nova senha",
    "reset.new": "Nova senha",
    "reset.confirm": "Confirmar senha",
    "reset.placeholder.confirm": "Digite a senha novamente",
    "reset.err.missing_token": "Token ausente na URL",
    "reset.err.too_short": "A senha deve ter pelo menos 8 caracteres",
    "reset.err.mismatch": "As senhas não coincidem",
    "reset.busy": "Salvando…",
    "reset.cta": "Definir nova senha",
    "reset.done":
      "✅ Senha atualizada. Redirecionando para o login…",
    "dash.welcome": "Bem-vindo, {name}",
    "dash.subtitle": "// {pct}% de receita · exibição em {currency}",
    "dash.kpi.signups": "Cadastros",
    "dash.kpi.active": "Pagadores ativos",
    "dash.kpi.earned": "Ganho",
    "dash.kpi.pending": "Pagamento pendente",
    "dash.how.title": "// como funciona",
    "dash.how.step1": "Crie um ou mais códigos em",
    "dash.how.step1_link": "Meus códigos",
    "dash.how.step1_dot": ".",
    "dash.how.step2_prefix": "Compartilhe o link",
    "dash.how.step2_suffix": "no seu canal.",
    "dash.how.step3":
      "Quando usuários atribuídos pagam pelo Pro, você ganha {pct}% da receita.",
    "dash.how.step4":
      "Solicite o pagamento quando seu saldo atingir ${threshold}.",
    "dash.payout_requested": "Pagamento solicitado.",
    "dash.payout_requested_hint": "Nossa equipe processará em breve.",
    "codes.title": "Meus códigos",
    "codes.subtitle":
      "// um código por canal · compartilhe para atribuir usuários",
    "codes.create": "Criar código",
    "codes.empty":
      "Ainda sem códigos — crie o primeiro para começar a atribuir.",
    "codes.col.code": "Código",
    "codes.col.label": "Rótulo",
    "codes.col.signups": "Cadastros",
    "codes.col.earned": "Ganho",
    "codes.col.status": "Status",
    "codes.row.links": "Links",
    "codes.row.hide": "Ocultar",
    "codes.row.enable": "Ativar",
    "codes.row.disable": "Desativar",
    "codes.link.telegram": "Telegram",
    "codes.link.web": "Web (ref)",
    "codes.modal.title": "Novo código",
    "codes.modal.code_label": "Código (3–32 caracteres · A–Z, 0–9, _, -)",
    "codes.modal.code_placeholder": "MEUCANAL26",
    "codes.modal.label_label": "Rótulo (opcional)",
    "codes.modal.label_placeholder": "Canal do YouTube",
    "codes.modal.busy": "Criando…",
    "codes.modal.cta": "Criar",
    "codes.created": "Código criado",
    "codes.activated": "Código ativo",
    "codes.disabled": "Código desativado",
    "codes.no_label": "sem rótulo",
    "tx.title": "Transações",
    "tx.subtitle":
      "// cada atribuição paga · faça uma compra de controle para verificar",
    "tx.total": "Total ganho (período)",
    "tx.tip":
      "💡 Dica: Faça uma pequena compra usando seu próprio link para verificar a atribuição. A transação aparece aqui em segundos.",
    "tx.col.datetime": "Data · hora",
    "tx.col.user": "Usuário",
    "tx.col.payment": "Pagamento",
    "tx.col.usd": "USD",
    "tx.col.commission": "% · comissão",
    "tx.empty":
      "Sem transações ainda — quando um usuário atribuído pagar, a comissão aparecerá aqui.",
    "payouts.title": "Pagamentos",
    "payouts.subtitle":
      "// solicite quando o saldo ≥ ${threshold} · pagamento manual",
    "payouts.available": "Saldo disponível",
    "payouts.threshold": "Limite:",
    "payouts.cta": "Solicitar pagamento",
    "payouts.cta.pending": "Solicitação pendente",
    "payouts.cta.below": "Abaixo do limite",
    "payouts.cta.busy": "Solicitando…",
    "payouts.requested": "Pagamento solicitado",
    "payouts.col.date": "Data",
    "payouts.col.usd": "USD",
    "payouts.col.native": "Local",
    "payouts.col.method": "Método",
    "payouts.col.status": "Status",
    "payouts.col.note": "Observação",
    "payouts.empty": "Sem pagamentos ainda.",
    "profile.title": "Perfil",
    "profile.subtitle": "// suas configurações & dados de pagamento",
    "profile.name": "Nome",
    "profile.email": "E-mail",
    "profile.email_hint":
      "Entre em contato com o suporte para alterar seu e-mail.",
    "profile.commission": "Comissão",
    "profile.commission_hint":
      "Definida pelo admin. Contate o suporte para renegociar.",
    "profile.currency": "Moeda de exibição",
    "profile.currency_hint":
      "Como seu saldo é exibido. A conversão ocorre na solicitação.",
    "profile.channels": "Canais de distribuição",
    "profile.channels_placeholder": "YouTube, canal Telegram…",
    "profile.payout_section": "// dados de pagamento",
    "profile.payout_intro":
      "Informe pelo menos um método para que possamos pagá-lo ao solicitar.",
    "profile.payout.phone": "Telefone",
    "profile.payout.card": "Número do cartão",
    "profile.payout.bank_account": "Conta bancária",
    "profile.payout.ton_wallet": "Carteira TON",
    "profile.payout.other": "Outro (CNPJ, IBAN…)",
    "profile.save": "Salvar perfil",
    "profile.saving": "Salvando…",
    "profile.saved": "Perfil salvo",
  },

  ru: {
    "common.cancel": "Отмена",
    "common.close": "Закрыть",
    "common.save": "Сохранить",
    "common.loading": "Загрузка…",
    "common.optional": "Необязательно",
    "common.email": "Email",
    "common.password": "Пароль",
    "common.back_to_signin": "← Вернуться к входу",
    "common.sign_in": "Войти",
    "common.sign_out": "Выйти",
    "common.apply": "Применить",
    "common.partners_subtitle": "// партнёры",
    "common.dash": "—",
    "nav.dashboard": "Главная",
    "nav.codes": "Мои коды",
    "nav.transactions": "Транзакции",
    "nav.payouts": "Выплаты",
    "nav.profile": "Профиль",
    "header.partner_cabinet": "// кабинет партнёра",
    "header.revenue_share_title": "Ваш процент с продаж",
    "header.theme_toggle_to": "Переключить на {target} тему",
    "login.subtitle": "// партнёры · вход",
    "login.placeholder.email": "you@example.com",
    "login.busy": "Авторизация…",
    "login.cta": "Войти",
    "login.forgot": "Забыли пароль?",
    "login.new_to": "Новичок в Clipra Partners?",
    "login.apply": "Подать заявку",
    "signup.subtitle.apply": "// партнёры · подать заявку",
    "signup.subtitle.claim": "// партнёры · принять приглашение",
    "signup.name": "Имя",
    "signup.placeholder.name": "Ваше имя или канал",
    "signup.placeholder.password": "Минимум 8 символов",
    "signup.invite_hint":
      "Принятие приглашения — администратор уже задал вашу комиссию и валюту.",
    "signup.busy.creating": "Создание аккаунта…",
    "signup.cta.create": "Создать аккаунт",
    "signup.cta.claim": "Принять приглашение",
    "signup.already": "Уже есть аккаунт?",
    "forgot.subtitle": "// партнёры · сброс пароля",
    "forgot.intro":
      "Введите email, с которым регистрировались — пришлём ссылку, действительную 1 час.",
    "forgot.busy": "Отправка…",
    "forgot.cta": "Отправить ссылку",
    "forgot.sent.body":
      "Если аккаунт для {email} существует, вы получите ссылку для сброса в течение минуты.",
    "forgot.sent.spam": "Не пришло? Проверьте папку «Спам» или",
    "forgot.sent.try_again": "попробуйте снова",
    "reset.subtitle": "// партнёры · придумайте новый пароль",
    "reset.new": "Новый пароль",
    "reset.confirm": "Подтвердите пароль",
    "reset.placeholder.confirm": "Повторите пароль",
    "reset.err.missing_token": "В URL отсутствует токен сброса",
    "reset.err.too_short": "Пароль должен быть минимум 8 символов",
    "reset.err.mismatch": "Пароли не совпадают",
    "reset.busy": "Сохранение…",
    "reset.cta": "Установить новый пароль",
    "reset.done": "✅ Пароль обновлён. Переадресация на вход…",
    "dash.welcome": "Добро пожаловать, {name}",
    "dash.subtitle":
      "// {pct}% с продаж · отображение в {currency}",
    "dash.kpi.signups": "Регистрации",
    "dash.kpi.active": "Активные плательщики",
    "dash.kpi.earned": "Заработано",
    "dash.kpi.pending": "К выплате",
    "dash.how.title": "// как это работает",
    "dash.how.step1": "Создайте один или несколько кодов в",
    "dash.how.step1_link": "Мои коды",
    "dash.how.step1_dot": ".",
    "dash.how.step2_prefix": "Поделитесь ссылкой",
    "dash.how.step2_suffix": "на своём канале.",
    "dash.how.step3":
      "Когда привлечённые пользователи покупают Pro, вы получаете {pct}% от выручки.",
    "dash.how.step4":
      "Запросите выплату, когда баланс достигнет ${threshold}.",
    "dash.payout_requested": "Выплата запрошена.",
    "dash.payout_requested_hint": "Наша команда обработает в ближайшее время.",
    "codes.title": "Мои коды",
    "codes.subtitle":
      "// один код на канал · поделитесь, чтобы привязать пользователей",
    "codes.create": "Создать код",
    "codes.empty":
      "Кодов пока нет — создайте первый, чтобы начать привязку пользователей.",
    "codes.col.code": "Код",
    "codes.col.label": "Метка",
    "codes.col.signups": "Регистрации",
    "codes.col.earned": "Заработано",
    "codes.col.status": "Статус",
    "codes.row.links": "Ссылки",
    "codes.row.hide": "Скрыть",
    "codes.row.enable": "Включить",
    "codes.row.disable": "Отключить",
    "codes.link.telegram": "Telegram",
    "codes.link.web": "Веб (ref)",
    "codes.modal.title": "Новый код",
    "codes.modal.code_label": "Код (3–32 символа · A–Z, 0–9, _, -)",
    "codes.modal.code_placeholder": "MYCHANNEL26",
    "codes.modal.label_label": "Метка (необязательно)",
    "codes.modal.label_placeholder": "YouTube канал",
    "codes.modal.busy": "Создание…",
    "codes.modal.cta": "Создать",
    "codes.created": "Код создан",
    "codes.activated": "Код активен",
    "codes.disabled": "Код отключён",
    "codes.no_label": "без метки",
    "tx.title": "Транзакции",
    "tx.subtitle":
      "// каждая оплаченная привязка · сделайте контрольную покупку для проверки",
    "tx.total": "Всего заработано (период)",
    "tx.tip":
      "💡 Совет: Сделайте небольшую покупку по своей ссылке, чтобы проверить привязку. Транзакция появится здесь за секунды.",
    "tx.col.datetime": "Дата · время",
    "tx.col.user": "Пользователь",
    "tx.col.payment": "Платёж",
    "tx.col.usd": "USD",
    "tx.col.commission": "% · комиссия",
    "tx.empty":
      "Транзакций пока нет — когда привязанный пользователь оплатит, комиссия появится здесь.",
    "payouts.title": "Выплаты",
    "payouts.subtitle":
      "// запрос при балансе ≥ ${threshold} · ручная выплата",
    "payouts.available": "Доступный баланс",
    "payouts.threshold": "Порог:",
    "payouts.cta": "Запросить выплату",
    "payouts.cta.pending": "Запрос в обработке",
    "payouts.cta.below": "Ниже порога",
    "payouts.cta.busy": "Запрос…",
    "payouts.requested": "Выплата запрошена",
    "payouts.col.date": "Дата",
    "payouts.col.usd": "USD",
    "payouts.col.native": "В валюте",
    "payouts.col.method": "Метод",
    "payouts.col.status": "Статус",
    "payouts.col.note": "Заметка",
    "payouts.empty": "Выплат пока нет.",
    "profile.title": "Профиль",
    "profile.subtitle": "// ваши настройки и реквизиты для выплат",
    "profile.name": "Имя",
    "profile.email": "Email",
    "profile.email_hint":
      "Чтобы изменить email — напишите в поддержку.",
    "profile.commission": "Комиссия",
    "profile.commission_hint":
      "Устанавливает администратор. Чтобы пересмотреть — напишите в поддержку.",
    "profile.currency": "Валюта отображения",
    "profile.currency_hint":
      "Как отображается ваш баланс. Конвертация — в момент запроса выплаты.",
    "profile.channels": "Каналы распространения",
    "profile.channels_placeholder": "YouTube, Telegram канал…",
    "profile.payout_section": "// реквизиты для выплат",
    "profile.payout_intro":
      "Укажите хотя бы один способ, чтобы мы могли перевести вам деньги.",
    "profile.payout.phone": "Телефон",
    "profile.payout.card": "Номер карты",
    "profile.payout.bank_account": "Банковский счёт",
    "profile.payout.ton_wallet": "TON кошелёк",
    "profile.payout.other": "Другое (ИНН, IBAN…)",
    "profile.save": "Сохранить профиль",
    "profile.saving": "Сохранение…",
    "profile.saved": "Профиль сохранён",
  },
};

function fmt(template, vars) {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (_, k) =>
    vars[k] !== undefined && vars[k] !== null ? String(vars[k]) : `{${k}}`,
  );
}

export function translate(key, lang, vars) {
  const dict = STRINGS[lang] || STRINGS[DEFAULT_LANG];
  const str = dict[key] ?? STRINGS[DEFAULT_LANG][key] ?? key;
  return fmt(str, vars);
}

// ── Zustand store + hook ────────────────────────────────────────────────

import { create } from "zustand";
import { useEffect } from "react";

export const useAffLang = create((set) => ({
  lang: DEFAULT_LANG,
  setLang: (lang) => {
    if (!LANGS.includes(lang)) return;
    persistLang(lang);
    set({ lang });
  },
  bootstrap: () => set({ lang: detectLang() }),
}));

export function useT() {
  const lang = useAffLang((s) => s.lang);
  return (key, vars) => translate(key, lang, vars);
}

export function useAffLangInit() {
  const bootstrap = useAffLang((s) => s.bootstrap);
  useEffect(() => {
    bootstrap();
  }, [bootstrap]);
}
